import {
    AppState,
    BreachEvent,
    click_origin_breach_screen,
    useFeature,
} from '@news-mono/web-common'
import React from 'react'
import { useSelector } from 'react-redux'
import { IconStar } from '../../../../../../icons/IconStar/IconStar'
import { useAuthActions } from '../../../../../../user-registration/authentication/AuthenticationProvider'
import { useSection } from '../../../../../../__helpers'
import { TheWestSection } from '../../../../../../__styling'
import { createLinkClickEvent } from '../../../../createLinkClickHandler'
import {
    Ribbon,
    StyledPackageImage,
    StyledSubscribeWithGoogleButton,
    StyledSubscriptionCancellationMessage,
    StyledSubscriptionPackage,
    StyledSubscriptionPackageActions,
    StyledSubscriptionPackageButton,
    StyledSubscriptionPackageHeading,
    StyledSubscriptionPackageOriginalPrice,
    StyledSubscriptionPackagePreTitle,
    StyledSubscriptionPackagePrice,
    StyledSubscriptionPackagePriceQualifier,
} from './BlackFridaySubscriptionPackage.styled'

const everyDayDigitalImage = require('../../../SubscriptionPackage/images/everyday_digital.png')
const everyDayNewspaperImage = require('../../../SubscriptionPackage/images/everyday_digital_newspaper.png')

export interface BlackFridaySubscriptionPackageProps {
    pretitle: string
    originalPrice?: string
    heading: string
    price: string
    priceQualifier?: string
    ribbonTitle?: string
    packagePath: string
    offerCode?: string
    packageType?: 'digital' | 'digitalprint'
    breachCreativeName: string
    breachDeal: string
    packageTheme?: 'default' | 'red'
    cancellationMessage?: string
    googleSKU?: string
    onEvent: (event: BreachEvent) => void
}

export const BlackFridaySubscriptionPackage: React.FC<BlackFridaySubscriptionPackageProps> = ({
    pretitle,
    heading,
    originalPrice,
    price,
    priceQualifier,
    ribbonTitle,
    packagePath,
    breachDeal,
    offerCode,
    breachCreativeName,
    packageTheme = 'default',
    onEvent,
    packageType,
    cancellationMessage,
    googleSKU,
}) => {
    const { onSubscribeClick } = useAuthActions()
    const subscribeWithGoogleEnabled = useFeature('subscribe-with-google')
    const section = useSection<TheWestSection>()

    // Matches swg-breach-albany-advertiser etc
    const subscribeWithGoogleBreachInterfaceEnabled = useFeature(
        `swg-breach-${section}`,
    )

    const { subscriptions } = useSelector((state: AppState) => state.SwG)

    const showSwG =
        subscribeWithGoogleEnabled &&
        subscribeWithGoogleBreachInterfaceEnabled &&
        googleSKU

    const handleSubscribeWithGoogleButton = (sku?: string) => {
        if (sku) {
            subscriptions?.subscribe(sku)
        }
    }

    return (
        // Note: There is high editorial care factor on these images, please ensure
        // appropriate approvals have happened before replacing or updating
        <StyledSubscriptionPackage packageTheme={packageTheme}>
            {packageType === 'digital' && (
                <StyledPackageImage
                    src={everyDayDigitalImage}
                    alt=""
                    role="presentation"
                />
            )}

            {packageType === 'digitalprint' && (
                <StyledPackageImage
                    src={everyDayNewspaperImage}
                    alt=""
                    role="presentation"
                />
            )}
            <StyledSubscriptionPackagePreTitle packageTheme={packageTheme}>
                {pretitle}
            </StyledSubscriptionPackagePreTitle>
            <StyledSubscriptionPackageHeading packageTheme={packageTheme}>
                {heading}
            </StyledSubscriptionPackageHeading>
            {originalPrice && (
                <StyledSubscriptionPackageOriginalPrice
                    packageTheme={packageTheme}
                >
                    {originalPrice}
                </StyledSubscriptionPackageOriginalPrice>
            )}
            <StyledSubscriptionPackagePrice packageTheme={packageTheme}>
                {price}
            </StyledSubscriptionPackagePrice>
            {priceQualifier && (
                <StyledSubscriptionPackagePriceQualifier
                    packageTheme={packageTheme}
                >
                    {priceQualifier}
                </StyledSubscriptionPackagePriceQualifier>
            )}
            {ribbonTitle && (
                <Ribbon packageTheme={packageTheme}>
                    <span>
                        <IconStar />
                        {ribbonTitle}
                    </span>
                </Ribbon>
            )}
            <StyledSubscriptionPackageActions>
                <StyledSubscriptionPackageButton
                    packageTheme={packageTheme}
                    buttonWidth="wide"
                    onClick={() => {
                        createLinkClickEvent(
                            breachCreativeName,
                            breachDeal,
                            onEvent,
                        )()
                        onSubscribeClick({
                            packagePath,
                            offerCode,
                            callToAction: click_origin_breach_screen,
                        })
                    }}
                >
                    Subscribe Now
                </StyledSubscriptionPackageButton>
                <StyledSubscriptionCancellationMessage
                    packageTheme={packageTheme}
                >
                    {cancellationMessage}
                </StyledSubscriptionCancellationMessage>
                {packageType === 'digital' && showSwG && (
                    <StyledSubscribeWithGoogleButton
                        onClick={() =>
                            handleSubscribeWithGoogleButton(googleSKU)
                        }
                        className="swg-button-dark"
                    ></StyledSubscribeWithGoogleButton>
                )}
            </StyledSubscriptionPackageActions>
        </StyledSubscriptionPackage>
    )
}

BlackFridaySubscriptionPackage.displayName = 'BlackFridaySubscriptionPackage'

export default BlackFridaySubscriptionPackage
